import {
  Box,
  Flex,
  IconButton,
  Text,
  useMediaQuery,
  useTheme,
} from '@chakra-ui/react'
import { ChevronUp, ChevronDown } from '@icon'
import { sideMenuAnim } from '@lib/motion'
import { motion } from 'framer-motion'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useRouter } from 'next/router'
import SwiperCore, { Controller, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { IHeadlineSlider } from '@interfaces'
import { HeroAspectRatio, Link } from '@ui'
import Vimeo from '@u-wave/react-vimeo'
import { useVimeoPlayer } from '@lib/hooks'
import { normalizeFeaturedButtonUrl } from '@utils'

// install Swiper modules
SwiperCore.use([Navigation, Controller])

// Banner görselleri: masaüstü 2560x1196 (2.14:1), mobil 853x1137 (0.75:1).
// Kabı görselin oranına sabitleyince backgroundSize: contain ile görsel tam
// oturur, kesilmez ve yanlarda boşluk kalmaz.
const HOME_HERO_RATIO = 2560 / 1196
const HOME_HERO_RATIO_MOBILE = 853 / 1137

export interface IHomeHero {
  swiperCollection: IHeadlineSlider[]
}

const HomeHero = observer(({ swiperCollection }: IHomeHero) => {
  const theme = useTheme()
  const { locale } = useRouter()
  const [isMobileScreen] = useMediaQuery('(max-width: 768px)')

  const [firstSwiper, setFirstSwiper] = useState(null)
  const [secondSwiper, setSecondSwiper] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)

  // Ana sayfa caption'ı diğer sayfalardaki banner'dan bağımsız olsun diye
  // global store yerine yerel state kullanılıyor (başlangıçta kapalı).
  const [displayBannerSlider, setDisplayBannerSlider] = useState(false)

  const {
    handlePlayerPause,
    handlePlayerPlay,
    getPausedVideoState,
    stopPlayingVideo,
  } = useVimeoPlayer(
    swiperCollection
      .filter((row) => row.isWithVideo)
      .map((video) => ({
        id: video.vimeoId,
        vimeoId: video.vimeoId,
        paused: true,
      }))
  )

  const autoPlayActiveVideo = (index: number) => {
    if (swiperCollection[index]?.isWithVideo) {
      setTimeout(() => {
        handlePlayerPlay(swiperCollection[index]?.vimeoId)
      }, 100)
    }
  }

  const swiperOnSlideChange = (swiper) => {
    stopPlayingVideo()
    autoPlayActiveVideo(swiper?.activeIndex)
    setActiveIndex(swiper?.activeIndex)
  }

  if (swiperCollection.length === 0 || !swiperCollection) {
    return (
      <HeroAspectRatio
        ratio={{ base: HOME_HERO_RATIO_MOBILE, lg: HOME_HERO_RATIO }}
        minHeight={{ base: 'auto', lg: 'calc(100vh - 268px)' }}
      >
        <Box
          width="full"
          height="full"
          backgroundImage="url('/img/home-page/banner/sabanci-banner.png')"
          backgroundPosition="center"
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
          position="relative"
          zIndex="1"
        />
      </HeroAspectRatio>
    )
  }

  return (
    <HeroAspectRatio
      ratio={{ base: HOME_HERO_RATIO_MOBILE, lg: HOME_HERO_RATIO }}
      minHeight={{ base: 'auto', lg: 'calc(100vh - 268px)' }}
    >
      <Box
        id="stickyFix"
        width="full"
        height="full"
        position="relative"
        zIndex="1"
        pt="0"
      >
        <Box
          bgColor="white.primary"
          width="full"
          height="full"
          overflow="hidden"
          sx={{ '.hero-bg-slider.swiper-container': { height: 'full' } }}
        >
          <Swiper
            pagination={{ el: '.swiper-pagination', type: 'fraction' }}
            navigation={{
              nextEl: '.shop-swiper-button-next',
              prevEl: '.shop-swiper-button-prev',
            }}
            noSwiping={true}
            className="hero-bg-slider"
            controller={{ control: secondSwiper }}
            onSwiper={setFirstSwiper}
            onSlideChange={swiperOnSlideChange}
          >
            {swiperCollection.map((row, index) => {
              const slideLinkUrl = row.linkUrl
                ? normalizeFeaturedButtonUrl(row.linkUrl, locale || 'tr')
                : ''
              const isSlideClickable =
                !displayBannerSlider && Boolean(slideLinkUrl)

              return (
                <SwiperSlide key={row.id}>
                  <Box width="full" height="full" position="relative">
                    {row.isWithVideo ? (
                      <Box
                        bgColor="black.primary"
                        height="full"
                        sx={{
                          '& .home-hero-iframe-video': {
                            width: 'full',
                            height: 'full',
                          },
                          '& .home-hero-iframe-video > iframe': {
                            width: 'full',
                            height: 'full',
                            pointerEvents: 'auto',
                          },
                        }}
                      >
                        <Vimeo
                          className="home-hero-iframe-video"
                          video={
                            isMobileScreen ? row.mobileVimeoId : row.vimeoId
                          }
                          paused={getPausedVideoState(row.vimeoId)}
                          onPause={() => handlePlayerPause(row.vimeoId)}
                          onPlay={() => handlePlayerPlay(row.vimeoId)}
                          color={theme.colors.red.primary}
                          showTitle={false}
                          loop={true}
                          volume={1}
                          muted={true}
                          {...(index === 0 && { autoplay: true })}
                        />
                      </Box>
                    ) : (
                      <Box
                        width="full"
                        height="100%"
                        backgroundImage={{
                          base: `url('${
                            row.mobileImgUrl || row.imgUrl || ''
                          }')`,
                          lg: `url('${row.imgUrl || row.mobileImgUrl || ''}')`,
                        }}
                        backgroundPosition="center"
                        backgroundSize="contain"
                        backgroundRepeat="no-repeat"
                        position="relative"
                        zIndex={displayBannerSlider ? 5 : 3}
                        py={{ base: 'padding.primary2', lg: '8' }}
                      />
                    )}
                    {isSlideClickable && (
                      <Link
                        href={slideLinkUrl}
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        zIndex={10}
                        cursor="pointer"
                        aria-label={row.linkLabel || row.title}
                        {...(row.isExternalLink && {
                          target: '_blank',
                          rel: 'noreferrer',
                        })}
                      />
                    )}
                  </Box>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </Box>

        <IconButton
          d="none"
          role="button"
          aria-pressed={displayBannerSlider}
          aria-label="Toggle banner slider"
          icon={
            <ChevronUp
              color="red.primary"
              fontSize={{ base: 'md', lg: 'xl' }}
              // transform={
              //   !displayBannerSlider ? 'rotate(90deg)' : 'rotate(0deg)'
              // }
              // transition="transform 0.3s ease-in-out"
            />
          }
          background="red.primary2"
          _hover={{ background: 'red.primary3' }}
          borderRadius="none"
          position="absolute"
          bottom={{ base: '-50px', lg: '-120px' }}
          right={0}
          width={{ base: '50px', lg: '120px' }}
          height={{ base: '50px', lg: '120px' }}
          onClick={() => setDisplayBannerSlider(!displayBannerSlider)}
          zIndex={2}
        />
        <Box
          position="absolute"
          bottom={{ base: '0px', lg: '0px' }}
          right={0}
          width={{ base: 'drawer.xs', lg: 'drawer.lg' }}
          height={{ base: '230px', lg: '300px' }}
          maxWidth="drawer.lg"
          maxHeight="drawer.lg"
          // zIndex={displayBannerSlider ? 1 : -1}
          zIndex={1}
          overflow="hidden"
          sx={{
            '.hero-slider-box.swiper-container': {
              height: 'full',
              position: 'relative',
            },
          }}
        >
          <Box
            width="full"
            height="full"
            as={motion.div}
            initial={false}
            animate={
              displayBannerSlider
                ? { ...sideMenuAnim.sliderMotion.visible, y: 0 }
                : {
                    ...sideMenuAnim.sliderMotion.hidden,
                    y: isMobileScreen ? '280px' : '330px',
                  }
            }
          >
            <Swiper
              onSwiper={setSecondSwiper}
              controller={{ control: firstSwiper }}
              pagination={{
                el: '.swiper-pagination',
                type: 'fraction',
              }}
              navigation={{
                nextEl: '.hero-slider-box-button-next',
                prevEl: '.hero-slider-box-button-prev',
              }}
              grabCursor={true}
              className="hero-slider-box"
            >
              {swiperCollection.map((content) => {
                if (swiperCollection[activeIndex]?.title.length < 1) {
                  return (
                    <SwiperSlide key={content.id}>
                      <Box
                        opacity={0.85}
                        as={motion.div}
                        width="full"
                        height="full"
                      ></Box>
                    </SwiperSlide>
                  )
                }
                return (
                  <SwiperSlide key={content.id}>
                    <Box
                      as={motion.div}
                      padding={{
                        base: 'padding.primary',
                        lg: 'padding.primary2',
                      }}
                      width="full"
                      height="full"
                      bgColor={{
                        base: 'rgba(199, 31, 63, 1)',
                        lg: "rgba(199, 31, 63, 1)"
                      }}
                    >
                      <Text
                        fontSize={{ base: 'md', lg: 'xl' }}
                        color="white.primary"
                        mb={0}
                        as="h6"
                        noOfLines={{ base: 3, lg: 2 }}
                      >
                        {content.title}
                      </Text>
                      <Text
                        fontSize={{ base: 'sm', lg: 'md' }}
                        fontWeight="light"
                        color="white.primary"
                        noOfLines={{ base: 3, lg: 2 }}
                      >
                        {content.description}
                      </Text>

                      {content.linkLabel && content.linkUrl && (
                        <Link
                          href={content.linkUrl}
                            color="white.primary"
                          fontSize={{ base: 'xs', lg: 'md' }}
                          fontWeight="light"
                          mt="3.5"
                          noOfLines={1}
                          target={content.isExternalLink ? '_blank' : '_self'}
                        >
                          {content.linkLabel}
                        </Link>
                      )}
                      <IconButton
                        d="none"
                        role="button"
                        aria-pressed={displayBannerSlider}
                        aria-label="Toggle banner slider"
                        icon={
                          <ChevronUp
                            color="white.primary"
                            fontSize={{ base: 'md', lg: 'xl' }}
                            transform={
                              !displayBannerSlider
                                ? 'rotate(90deg)'
                                : 'rotate(0deg)'
                            }
                            transition="transform 0.3s ease-in-out"
                          />
                        }
                        background={content.bgColorChevron || 'red.primary2'}
                        _hover={{ background: 'red.primary3' }}
                        borderRadius="none"
                        position="absolute"
                        bottom={0}
                        right={0}
                        //  bottom={{ base: '-100px', lg: '-120px' }}
                        // right={0}
                        width={{ base: '50px', lg: '70px' }}
                        height={{ base: '50px', lg: '70px' }}
                        onClick={() =>
                          setDisplayBannerSlider(!displayBannerSlider)
                        }
                        zIndex={4}
                      />
                    </Box>
                  </SwiperSlide>
                )
              })}
              <Flex
                position="absolute"
                bottom={0}
                right={0}
                width="full"
                justifyContent="space-between"
                sx={{
                  '.hero-slider-box-button-next, .hero-slider-box-button-prev':
                    {
                      width: '16px',
                      height: 'auto',
                      color: 'white.primary2',
                    },
                  '.hero-slider-box-button-next:after, .hero-slider-box-button-prev:after':
                    {
                      fontSize: 'md',
                    },
                  '.hero-slider-box-button-next.swiper-button-disabled, .hero-slider-box-button-prev.swiper-button-disabled':
                    {
                      opacity: 1,
                    },
                }}
                bgColor={{
                  base:
                    swiperCollection[activeIndex]?.title.length < 1
                      ? 'red.primary'
                      : 'initial',
                }}
              >
                <Flex
                  alignItems="baseline"
                  pl={{
                    base:
                      swiperCollection[activeIndex]?.title.length < 1
                        ? 'padding.primary'
                        : 'padding.primary',
                    lg:
                      swiperCollection[activeIndex]?.title.length < 1
                        ? 'padding.primary2'
                        : 'padding.primary2',
                  }}
                  pb={{
                    base:
                      swiperCollection[activeIndex]?.title.length < 1
                        ? '10px'
                        : '10px',
                    lg:
                      swiperCollection[activeIndex]?.title.length < 1
                        ? '20px'
                        : '20px',
                  }}
                >
                  <Box
                    role="button"
                    aria-label="Hero slider box previous button"
                    className="swiper-button-prev hero-slider-box-button-prev"
                    position="static"
                  />
                  <Box
                    className="swiper-pagination"
                    position="static"
                    mx={5}
                    width="auto"
                    color="white.primary"
                    fontSize="sm"
                    sx={{
                      '.swiper-pagination-current': {
                        fontSize: '32px',
                        fontWeight: 'regular',
                      },
                    }}
                  />
                  <Box
                    role="button"
                    aria-label="Hero slider box next button"
                    position="static"
                    className="swiper-button-next hero-slider-box-button-next"
                    sx={{
                      '&:after': {
                        fontsize: 'md',
                      },
                    }}
                  />
                </Flex>
              </Flex>
            </Swiper>
          </Box>
        </Box>
        <IconButton
          aria-label="Toggle banner slider"
          icon={
            <Box
              as={motion.div}
              animate={{ rotate: displayBannerSlider ? 0 : 180 }}
              transition={{ duration: 0.4, ease: [0.83, 0, 0.17, 1] }}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ChevronDown color="red.primary4" fontSize="24px" />
            </Box>
          }
          background="red.primary"
          _hover={{ background: 'red.primary1' }}
          borderRadius="none"
          position="absolute"
          bottom={{ base: '-50px', lg: '-90px' }}
          right={0}
          width={{ base: '50px', lg: '90px' }}
          height={{ base: '50px', lg: '90px' }}
          onClick={() => setDisplayBannerSlider(!displayBannerSlider)}
          zIndex={6}
        />
      </Box>
    </HeroAspectRatio>
  )
})

export default HomeHero
